import React, { useState } from 'react';

const REFLEX_GRADES = [
  { id: '0',   label: '0',   desc: 'Absent',        color: '#991b1b', bg: '#fff1f2', border: '#fda4af' },
  { id: '1+',  label: '1+',  desc: 'Hyporeflexia',  color: '#ea580c', bg: '#fff7ed', border: '#fdba74' },
  { id: '2+',  label: '2+',  desc: 'Normal',        color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  { id: '3+',  label: '3+',  desc: 'Hyperreflexia', color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
  { id: '4+',  label: '4+',  desc: 'Clonus / ↑↑',  color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
];

const DEEP_REFLEXES = [
  { id: 'biceps',          label: 'Biceps',          nerve: 'C5, C6',   category: 'Upper Limb' },
  { id: 'brachioradialis', label: 'Brachioradialis', nerve: 'C5, C6',   category: 'Upper Limb' },
  { id: 'triceps',         label: 'Triceps',         nerve: 'C6, C7',   category: 'Upper Limb' },
  { id: 'knee',            label: 'Knee (Patellar)', nerve: 'L3, L4',   category: 'Lower Limb' },
  { id: 'ankle',           label: 'Ankle (Achilles)',nerve: 'S1, S2',   category: 'Lower Limb' },
];

const SUPERFICIAL_REFLEXES = [
  { id: 'abdominal_upper', label: 'Abdominal (Upper)', nerve: 'T8-T10', options: ['Present','Absent'] },
  { id: 'abdominal_lower', label: 'Abdominal (Lower)', nerve: 'T10-T12', options: ['Present','Absent'] },
  { id: 'plantar_rt',      label: 'Plantar – Right',   nerve: 'S1',     options: ['Flexor (Normal)','Extensor – Babinski (+)','No response'] },
  { id: 'plantar_lt',      label: 'Plantar – Left',    nerve: 'S1',     options: ['Flexor (Normal)','Extensor – Babinski (+)','No response'] },
  { id: 'cremasteric',     label: 'Cremasteric',        nerve: 'L1, L2', options: ['Present','Absent'] },
  { id: 'anal_wink',       label: 'Anal Wink',          nerve: 'S3, S4', options: ['Present','Absent'] },
];

const PATHOLOGICAL_SIGNS = [
  { id: 'babinski',  label: 'Babinski sign',      side: true  },
  { id: 'hoffman',   label: 'Hoffman sign',        side: true  },
  { id: 'clonus',    label: 'Clonus',              side: true  },
  { id: 'oppenheim', label: 'Oppenheim sign',      side: true  },
  { id: 'gordon',    label: "Gordon's sign",       side: true  },
  { id: 'chaddock',  label: "Chaddock's sign",     side: true  },
  { id: 'rossolimo', label: "Rossolimo's sign",    side: true  },
  { id: 'trommer',   label: "Tromner's sign",      side: true  },
  { id: 'jaw_jerk',  label: 'Jaw jerk (↑)',        side: false },
  { id: 'glabellar', label: 'Glabellar tap (+)',   side: false },
  { id: 'grasp',     label: 'Grasp reflex',        side: true  },
  { id: 'snout',     label: 'Snout reflex',        side: false },
];

const ReflexAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const get = (key) => value[key] || null;
  const set = (key, val) => onChange?.({ ...value, [key]: value[key] === val ? null : val });
  const setText = (key, val) => onChange?.({ ...value, [key]: val });

  const abnormalDeep = DEEP_REFLEXES.filter(r =>
    ['rt','lt'].some(s => { const v = get(`${r.id}_${s}`); return v && v !== '2+'; })
  ).length;
  const babinski = PATHOLOGICAL_SIGNS.filter(p => get(`path_${p.id}`)).length;
  const totalAbnormal = abnormalDeep + babinski;

  const ReflexGradeCell = ({ refId, side }) => {
    const key = `${refId}_${side}`;
    const selected = get(key);
    const gr = REFLEX_GRADES.find(g => g.id === selected);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {REFLEX_GRADES.map(g => {
            const active = selected === g.id;
            return (
              <button key={g.id} type="button" onClick={() => set(key, g.id)} style={{
                width: '34px', height: '30px', borderRadius: '8px',
                border: `1.5px solid ${active ? g.border : '#e2e8f0'}`,
                background: active ? g.bg : 'transparent',
                color: active ? g.color : '#94a3b8',
                fontSize: '11px', fontWeight: 800, cursor: 'pointer', transition: 'all 0.15s'
              }}>{g.label}</button>
            );
          })}
        </div>
        {gr && gr.id !== '2+' && (
          <div style={{ fontSize: '10px', color: gr.color, fontWeight: 700 }}>{gr.desc}</div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px',
          borderRadius: '14px', border: '1px solid #e2e8f0',
          backgroundColor: 'transparent', color: '#9f1239',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
          transition: 'all 0.2s'
        }}>
          + Reflex Assessment
          {totalAbnormal > 0 && (
            <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '20px', background: '#9f1239', color: '#fff', fontWeight: 800 }}>
              {totalAbnormal} abnormal finding{totalAbnormal > 1 ? 's' : ''}
            </span>
          )}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <span style={{ fontWeight: '800', fontSize: '1rem', color: '#9f1239' }}>🔨 Reflex Assessment</span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: '#cbd5e1', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}
              onMouseEnter={e => e.target.style.color = '#ef4444'}
              onMouseLeave={e => e.target.style.color = '#cbd5e1'}>×</button>
          </div>

          {/* Summary bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '14px',
            padding: '10px 16px', borderRadius: '12px',
            background: totalAbnormal > 0 ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${totalAbnormal > 0 ? '#fca5a5' : '#6ee7b7'}`,
            marginBottom: '14px', flexWrap: 'wrap'
          }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: totalAbnormal > 0 ? '#dc2626' : '#059669' }}>
              {totalAbnormal === 0 ? 'No abnormal reflexes documented yet' : `${totalAbnormal} abnormal finding${totalAbnormal > 1 ? 's' : ''} recorded`}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {REFLEX_GRADES.map(g => (
                <span key={g.id} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: g.bg, color: g.color, border: `1px solid ${g.border}`, fontWeight: 700 }}>
                  {g.label} = {g.desc}
                </span>
              ))}
            </div>
          </div>

          {/* DEEP REFLEXES */}
          <div style={{ marginBottom: '12px', overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #fca5a5', background: '#fff', minWidth: '380px' }}>
            <div style={{ background: '#fef2f2', padding: '8px 14px', borderBottom: '0.5px solid #fca5a5' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 80px 1fr 1fr', alignItems: 'center', gap: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#9f1239', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Deep (DTRs)</div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#9f1239', textAlign: 'center' }}>Nerve</div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#9f1239', textAlign: 'center' }}>Left</div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#9f1239', textAlign: 'center' }}>Right</div>
              </div>
            </div>
            {['Upper Limb','Lower Limb'].map(cat => {
              const catReflexes = DEEP_REFLEXES.filter(r => r.category === cat);
              return (
                <div key={cat}>
                  <div style={{ padding: '5px 14px', background: '#fafafa', borderBottom: '0.5px solid #f1f5f9', borderTop: '0.5px solid #f1f5f9' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{cat}</div>
                  </div>
                  {catReflexes.map((r, idx) => {
                    const ltAbnormal = get(`${r.id}_lt`) && get(`${r.id}_lt`) !== '2+';
                    const rtAbnormal = get(`${r.id}_rt`) && get(`${r.id}_rt`) !== '2+';
                    return (
                      <div key={r.id} style={{
                        display: 'grid', gridTemplateColumns: '160px 80px 1fr 1fr',
                        alignItems: 'center', padding: '10px 14px', gap: '8px',
                        borderBottom: idx < catReflexes.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                        background: (ltAbnormal || rtAbnormal) ? '#fff8f8' : '#fff'
                      }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{r.label}</div>
                          {(ltAbnormal || rtAbnormal) && (
                            <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 700 }}>Abnormal</span>
                          )}
                        </div>
                        <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center' }}>{r.nerve}</div>
                        <ReflexGradeCell refId={r.id} side="lt" />
                        <ReflexGradeCell refId={r.id} side="rt" />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          </div>

          {/* SUPERFICIAL REFLEXES */}
          <div style={{ marginBottom: '12px', overflowX: 'auto', borderRadius: '12px' }}>
          <div style={{ border: '0.5px solid #fde68a', background: '#fff', minWidth: '380px' }}>
            <div style={{ background: '#fffbeb', padding: '8px 14px', borderBottom: '0.5px solid #fde68a' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Superficial Reflexes</div>
            </div>
            {SUPERFICIAL_REFLEXES.map((r, idx) => {
              const selected = get(`surf_${r.id}`);
              const isAbnormal = selected && (selected.includes('Absent') || selected.includes('Babinski') || selected.includes('No response'));
              return (
                <div key={r.id} style={{
                  display: 'grid', gridTemplateColumns: '180px 70px 1fr',
                  alignItems: 'center', padding: '10px 14px', gap: '8px',
                  borderBottom: idx < SUPERFICIAL_REFLEXES.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                  background: isAbnormal ? '#fff8f0' : '#fff'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{r.label}</div>
                    {isAbnormal && <div style={{ fontSize: '10px', color: '#ea580c', fontWeight: 700 }}>Abnormal</div>}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>{r.nerve}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {r.options.map(opt => {
                      const active = selected === opt;
                      const isNeg = opt.includes('Normal') || opt.includes('Present') || opt.includes('Flexor');
                      const activeColor = isNeg ? '#059669' : '#dc2626';
                      const activeBg = isNeg ? '#ecfdf5' : '#fef2f2';
                      const activeBorder = isNeg ? '#6ee7b7' : '#fca5a5';
                      return (
                        <button key={opt} type="button" onClick={() => set(`surf_${r.id}`, opt)} style={{
                          fontSize: '11px', fontWeight: 600, padding: '4px 11px', borderRadius: '20px', cursor: 'pointer',
                          transition: 'all 0.15s',
                          border: `1px solid ${active ? activeBorder : '#e2e8f0'}`,
                          background: active ? activeBg : 'transparent',
                          color: active ? activeColor : '#64748b'
                        }}>{active ? '✓ ' : ''}{opt}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          </div>

          {/* PATHOLOGICAL SIGNS */}
          <div style={{ border: '0.5px solid #ddd6fe', borderRadius: '12px', overflow: 'hidden', background: '#fff' }}>
            <div style={{ background: '#f5f3ff', padding: '8px 14px', borderBottom: '0.5px solid #ddd6fe' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pathological Signs</div>
            </div>
            <div style={{ padding: '12px 14px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                {PATHOLOGICAL_SIGNS.map(p => {
                  const selected = get(`path_${p.id}`);
                  const active = !!selected;
                  return (
                    <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
                      <button type="button" onClick={() => set(`path_${p.id}`, p.side ? 'bilateral' : 'present')} style={{
                        fontSize: '11px', fontWeight: 600, padding: '5px 12px', borderRadius: '20px', cursor: 'pointer',
                        transition: 'all 0.15s',
                        border: `1px solid ${active ? '#fca5a5' : '#e2e8f0'}`,
                        background: active ? '#fef2f2' : 'transparent',
                        color: active ? '#dc2626' : '#64748b'
                      }}>{active ? '✓ ' : ''}{p.label}</button>
                      {active && p.side && (
                        <div style={{ display: 'flex', gap: '3px' }}>
                          {['Left','Right','Bilateral'].map(s => {
                            const sideSel = selected === s.toLowerCase();
                            return (
                              <button key={s} type="button" onClick={() => onChange?.({ ...value, [`path_${p.id}`]: s.toLowerCase() })} style={{
                                fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '4px', cursor: 'pointer',
                                border: `1px solid ${sideSel ? '#7c3aed' : '#e2e8f0'}`,
                                background: sideSel ? '#f5f3ff' : 'transparent',
                                color: sideSel ? '#7c3aed' : '#94a3b8'
                              }}>{s}</button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', marginBottom: '5px' }}>Additional pathological notes:</div>
                <textarea
                  rows={2}
                  placeholder="e.g. Ankle clonus 5 beats bilateral, Babinski present right..."
                  value={value.pathological_notes || ''}
                  onChange={e => setText('pathological_notes', e.target.value)}
                  style={{ width: '100%', fontSize: '12px', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', color: '#374151', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>

          {Object.keys(value).length > 0 && (
            <button type="button" onClick={() => onChange({})} style={{ marginTop: '10px', fontSize: '12px', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>
              Clear all reflex data
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReflexAssessment;
